/**
 * VoiceBar Component
 * Bottom bar UI for voice interaction
 */

import React from 'react';
import clsx from 'clsx';
import type { VoiceState } from '@ai360smartsearch/shared';
import { AudioWaveform } from './AudioWaveform';
import './VoiceBar.css';

interface VoiceBarProps {
  voiceState: VoiceState;
  transcript?: string;
  onStart: () => void;
  onStop: () => void;
  onClose: () => void;
}

export const VoiceBar: React.FC<VoiceBarProps> = ({
  voiceState,
  transcript,
  onStart,
  onStop,
  onClose,
}) => {
  const getStatusText = () => {
    switch (voiceState) {
      case 'listening':
        return 'Escuchando...';
      case 'processing':
        return 'Procesando...';
      case 'speaking':
        return 'Hablando...';
      default:
        return 'Toca para hablar';
    }
  };

  const isActive = voiceState !== 'idle';

  return (
    <div className={clsx('ai360-voice-bar', isActive && 'ai360-voice-bar--active')}>
      <div className="ai360-voice-bar-content">
        <button
          type="button"
          onClick={isActive ? onStop : onStart}
          className={clsx(
            'ai360-voice-bar-button',
            `ai360-voice-bar-button--${voiceState}`
          )}
          disabled={voiceState === 'processing' || voiceState === 'speaking'}
        >
          {voiceState === 'listening' ? (
            <AudioWaveform isActive={true} />
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path d="M19 10v2a7 7 0 1 1-14 0v-2" stroke="currentColor" strokeWidth="2" />
            </svg>
          )}
        </button>

        <div className="ai360-voice-bar-info">
          <p className="ai360-voice-bar-status">{getStatusText()}</p>
          {transcript && (
            <p className="ai360-voice-bar-transcript">{transcript}</p>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="ai360-voice-bar-close"
          aria-label="Cerrar"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
